import React, { useCallback, useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Award, Download, LogOut } from 'lucide-react'
import SEO from '@components/SEO'
import { useFirebaseAuth } from '@context/FirebaseAuthContext'
import { fetchCertificationAttemptsList } from '@lib/certificationFirestore'
import { downloadCertificateFromAttempt } from '@lib/generateCertificatePdf'
import { CERTIFICATION_BTN_OUTLINE_DARK } from '@lib/certificationUi'

function formatSubmittedAt(data) {
  const ts = data?.createdAt
  if (ts && typeof ts.toDate === 'function') {
    return ts.toDate().toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    })
  }
  return data?.completedDateLabel || '—'
}

export default function CertificationMyResultsPage() {
  const { user, loading: authLoading, signOutUser } = useFirebaseAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  const load = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setLoadError('')
    try {
      const list = await fetchCertificationAttemptsList(50)
      setRows(list)
    } catch (e) {
      console.error(e)
      setLoadError('We could not load your history. Check your connection and try again.')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (user) void load()
  }, [user, load])

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-neutral-400 text-sm">Loading your account…</p>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/certification/sign-in?next=%2Fcertification%2Fmy-results" replace />
  }

  const phoneLabel = user.phoneNumber || 'your mobile number'

  return (
    <div className="bg-black min-h-screen pb-24">
      <SEO
        title="My certification results | Total Rewards Pro"
        description="Review your past Total Rewards practice assessments, scores, and download certificates."
      />
      <section className="pt-14 lg:pt-16 pb-10 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950 to-black opacity-90" />
        <div className="w-full px-6 sm:px-8 lg:px-12 relative max-w-4xl mx-auto">
          <Link
            to="/certification"
            className="inline-flex items-center text-sm text-primary-400 hover:text-primary-300 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Certification home
          </Link>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-display-2 text-white mb-2">Your progress</h1>
                <p className="text-body text-neutral-400 max-w-xl leading-relaxed">
                  Signed in as <span className="text-white font-medium">{phoneLabel}</span>. Here is every assessment
                  you have submitted on this account. Passed attempts include a certificate you can download again at
                  any time.
                </p>
              </div>
              <button
                type="button"
                onClick={() => void signOutUser()}
                className={`${CERTIFICATION_BTN_OUTLINE_DARK} shrink-0 gap-2 self-start`}
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>

            {loadError ? (
              <div className="rounded-lg border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span>{loadError}</span>
                <button
                  type="button"
                  className="btn-accent text-xs py-2 px-4 shrink-0 self-start sm:self-auto"
                  onClick={() => void load()}
                >
                  Try again
                </button>
              </div>
            ) : null}

            {loading ? (
              <p className="text-sm text-neutral-500">Fetching your attempts…</p>
            ) : rows.length === 0 ? (
              <div className="glass-effect-dark rounded-2xl border border-primary-500/20 p-10 text-center">
                <p className="text-body text-neutral-300 mb-6">
                  You have not submitted an assessment yet. When you do, your score and certificate (if you pass) will
                  appear here.
                </p>
                <Link to="/certification" className="btn-accent inline-flex items-center gap-2">
                  Choose a track
                </Link>
              </div>
            ) : (
              <ul className="space-y-4">
                {rows.map((row) => (
                  <li
                    key={row.id}
                    className="glass-effect-dark rounded-xl border border-primary-500/15 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                  >
                    <div className="min-w-0 space-y-1">
                      <p className="text-xs text-primary-400 font-semibold uppercase tracking-wide">
                        {row.trackLabel || row.trackId || 'Track'}
                      </p>
                      <p className="text-white font-semibold truncate">{row.quizTitle || 'Assessment'}</p>
                      <p className="text-sm text-neutral-400">
                        {formatSubmittedAt(row)} · Score{' '}
                        <span className="text-white">{row.scoreText || `${row.rawScore} / ${row.maxPoints}`}</span>
                        {row.passed ? (
                          <span className="ml-2 text-primary-400">· Passed</span>
                        ) : (
                          <span className="ml-2 text-neutral-500">· Not passed</span>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      {row.passed === true && row.certificateId ? (
                        <button
                          type="button"
                          className="btn-accent inline-flex items-center gap-2 text-sm py-2.5 px-4"
                          onClick={() => {
                            void downloadCertificateFromAttempt(row).catch((err) => {
                              console.error(err)
                              window.alert('Could not download the certificate PDF. Please try again.')
                            })
                          }}
                        >
                          <Download className="w-4 h-4" />
                          PDF
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-neutral-500 px-2 py-2">
                          <Award className="w-4 h-4" />
                          No certificate
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  )
}
